import React, { useState } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useAuth } from "src/contexts/AuthContext";
import { AppShell } from "src/components/layout/AppShell";
import { CONFIGURATIONS_NAV } from "src/config/configurationsNav";
import { cn } from "src/utils/cn";

// Hide config items the current member can't use. `hideForRoles` hides the
// item from a denylist of roles; `requires` checks a permission via can();
// `adminOnly` checks the org_admin role; `roles` checks membership in an
// allowed role list.
function visibleItems(items, { can, role }) {
  return items.filter((item) => {
    if (item.hideForRoles?.includes(role)) return false;
    if (item.adminOnly) return ["org_admin", "site_head", "team_lead"].includes(role);
    if (item.roles) return item.roles.includes(role);
    if (item.requires) return can(item.requires.resource, item.requires.action);
    return true;
  });
}

export default function ConfigurationsLayout() {
  const { organization, can, currentMembership } = useAuth();
  const location = useLocation();
  const orgSlug = organization?.slug || "general";
  const role = currentMembership?.role?.slug;

  // State to track which sections are open/collapsed
  const [openSections, setOpenSections] = useState(() => {
    const initial = {};
    CONFIGURATIONS_NAV.forEach((section) => {
      initial[section.sectionLabel] = section.defaultOpen ?? true;
    });
    return initial;
  });

  const toggleSection = (label) => {
    setOpenSections((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  return (
    <AppShell>
      <div className="flex h-full flex-col bg-white">
        {/* Header bar */}
        <div className="flex h-14 shrink-0 items-center border-b border-slate-100 px-6">
          <h1 className="text-[17px] font-bold text-slate-900">Configuration</h1>
        </div>

        {/* Main nested layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sub-navigation sidebar - fixed width of 240px, never shrinks */}
          <aside
            className="flex shrink-0 flex-col overflow-y-auto border-r border-slate-100 bg-slate-50/40 px-3 py-4"
            style={{
              width: "240px",
              minWidth: "240px",
              maxWidth: "240px",
            }}
          >
            <div className="space-y-4">
              {CONFIGURATIONS_NAV.map((section) => {
                // A section can be hidden outright for certain roles (e.g.
                // callers never see the "Organization" section).
                if (section.hideForRoles?.includes(role)) return null;

                const items = visibleItems(section.items, { can, role });
                // Skip a section entirely when the member can't see any of its
                // items (e.g. "Core" is empty for a caller) so we don't render a
                // dangling header.
                if (items.length === 0) return null;

                const isOpen = openSections[section.sectionLabel];
                return (
                  <div key={section.sectionLabel} className="space-y-1">
                    {/* Section Header */}
                    <button
                      onClick={() => toggleSection(section.sectionLabel)}
                      className="flex w-full items-center justify-between px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-400 transition-colors hover:text-slate-600"
                    >
                      <span>{section.sectionLabel}</span>
                      {isOpen ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                      )}
                    </button>

                    {/* Section Items */}
                    {isOpen && (
                      <div className="space-y-0.5">
                        {items.map((item) => {
                          const resolvedHref = item.href.replace(":slug", orgSlug);
                          const isOrgEditPattern = /^\/organizations\/[^/]+\/edit$/.test(location.pathname);
                          const isActive = location.pathname === resolvedHref ||
                            (item.id === "config-general" && (location.pathname === "/configurations/general" || isOrgEditPattern));
                          const Icon = item.icon;

                          return (
                            <Link
                              key={item.id}
                              to={resolvedHref}
                              className={cn(
                                "flex items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] font-medium transition-colors shrink-0",
                                isActive
                                  ? "bg-slate-100 font-semibold text-slate-900 ring-1 ring-slate-300"
                                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                              )}
                            >
                              {Icon && (
                                <Icon
                                  className={cn(
                                    "h-4 w-4 shrink-0",
                                    isActive ? "text-slate-700" : "text-slate-400"
                                  )}
                                />
                              )}
                              <span className="truncate">{item.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </aside>

          {/* Content Area */}
          <main className="flex-1 overflow-y-auto bg-white p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </AppShell>
  );
}
